/* eslint-disablereact/jsx-props-no-spreading */
import { Button, IconButton, Typography } from '@mui/material'
import Grid from '@mui/material/GridLegacy'
import heic2any from 'heic2any'
import { useState } from 'react'
import type { ImageListType } from 'react-images-uploading'
import ImageUploading from 'react-images-uploading'
import { sprintf } from 'sprintf-js'
import { FoxIconAdd, FoxIconCamera, FoxIconMinusDashHorizontalLine } from 'shared/src/components/font/FoxIcon'
import type { StrictImageType } from 'shared/src/components/imageUpload/types'
import { FoxColor } from 'shared/src/theme/foxColor'
import { resizeImageIfTooBig } from 'shared/src/util/blobUtil'
import { T } from 'shared/src/util/translation'
import { isValue } from 'shared/src/util/typeGuard'
import 'shared/src/components/imageUpload/ImageUpload.scss'

const ACCEPTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'jfif', 'heif', 'heic']
const HEIF_EXTENSIONS = ['heic', 'heif']

interface Props {
  images: StrictImageType[]
  uploadActionLabel?: string
  buttonLabel?: string
  maxImages?: number
  updateFormState: (images: StrictImageType[]) => void
  maxFileSizeByte: number
}

const draggingStyle = {
  color: FoxColor.blue800,
  backgroundColor: FoxColor.blue150,
  borderColor: FoxColor.blue800,
}

export const ImageUpload = ({
  images,
  uploadActionLabel,
  buttonLabel = 'take_a_picture_or_upload_a_screenshot',
  maxImages = 6,
  updateFormState,
  maxFileSizeByte,
}: Props) => {
  const [errorUploadImageMessage, setErrorUploadImageMessage] = useState('')

  const handleOnChangeImages = (imageList: ImageListType) => {
    setErrorUploadImageMessage('')
    const HEIF_CONVERSION_ERROR = Symbol('heif_error')
    type ImageResult = StrictImageType | null | typeof HEIF_CONVERSION_ERROR
    const tempImages: Promise<ImageResult>[] = []
    for (const image of imageList) {
      const { file, dataURL } = image
      if (!isValue(file) || !isValue(dataURL)) {
        // skip invalid entries
      } else {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
        const isHeif = HEIF_EXTENSIONS.includes(ext)

        if (isHeif) {
          tempImages.push(
            heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 })
              .then((converted) => {
                const blob = Array.isArray(converted) ? converted[0] : converted
                return new Promise<ImageResult>((resolve) => {
                  const reader = new FileReader()
                  reader.onload = () => {
                    const jpegDataURL = String(reader.result)
                    const jpegFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })
                    resolve(resizeImageIfTooBig({ dataURL: jpegDataURL, file: jpegFile }, maxFileSizeByte))
                  }
                  reader.onerror = () => resolve(HEIF_CONVERSION_ERROR)
                  reader.readAsDataURL(blob)
                })
              })
              .catch(() => HEIF_CONVERSION_ERROR),
          )
        } else if (dataURL.includes('image/')) {
          tempImages.push(resizeImageIfTooBig({ dataURL, file }, maxFileSizeByte))
        }
      }
    }
    Promise.all(tempImages).then((results) => {
      const hasHeifError = results.some((result) => result === HEIF_CONVERSION_ERROR)
      if (hasHeifError) {
        setErrorUploadImageMessage(T('error_heif_conversion_failed'))
      }
      const validImages = results.filter((r): r is StrictImageType => isValue(r) && r !== HEIF_CONVERSION_ERROR)
      updateFormState(validImages)
    })
  }

  return (
    <ImageUploading
      multiple
      value={images}
      onChange={handleOnChangeImages}
      allowNonImageType
      acceptType={ACCEPTED_IMAGE_EXTENSIONS}
      maxNumber={maxImages}
      onError={(errors) => {
        if (isValue(errors)) {
          if (isValue(errors.maxNumber) && errors.maxNumber) {
            setErrorUploadImageMessage(sprintf(T('error_too_many_images'), maxImages))
          } else if (isValue(errors.acceptType) && errors.acceptType) {
            setErrorUploadImageMessage(T('error_image_upload_types'))
          }
        }
      }}
    >
      {({ imageList, onImageUpload, onImageRemove, isDragging, dragProps }) => (
        <div className='upload__image-wrapper'>
          {uploadActionLabel !== '' ? (
            <div className='upload__image-action-label'>
              <Typography variant='body1' component='div' align='left'>
                {uploadActionLabel}
              </Typography>
            </div>
          ) : null}
          {imageList.length === 0 ? (
            <Button
              startIcon={<FoxIconCamera />}
              className='upload__image-button'
              variant='outlined'
              fullWidth
              onClick={onImageUpload}
              style={isDragging ? draggingStyle : undefined}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...dragProps}
            >
              <span>{T(buttonLabel)}</span>
            </Button>
          ) : (
            <Grid container direction='row' justifyContent='flex-start'>
              {imageList.map((image, index) => (
                <Grid key={image.dataURL} item container justifyContent='flex-start' width='fit-content'>
                  <Grid item>
                    <div key={image.file?.name} className='image-item'>
                      <img src={image.dataURL} alt='' />
                    </div>
                  </Grid>
                  <Grid item>
                    <IconButton className='image-item__btn-wrapper' onClick={() => onImageRemove(index)}>
                      <FoxIconMinusDashHorizontalLine />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              {imageList.length < 6 ? (
                <Grid item>
                  <IconButton
                    className='image-add__btn-wrapper'
                    onClick={onImageUpload}
                    style={isDragging ? draggingStyle : undefined}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...dragProps}
                  >
                    <FoxIconAdd />
                  </IconButton>
                </Grid>
              ) : null}
            </Grid>
          )}
          {errorUploadImageMessage !== '' ? (
            <div className='upload__image-error'>
              <Typography variant='body1' component='div' align='left'>
                {errorUploadImageMessage}
              </Typography>
            </div>
          ) : null}
        </div>
      )}
    </ImageUploading>
  )
}
